import { createContext, useContext, useState, useEffect } from 'react';
import { user as userApi } from '../services/api';
import { syncFromPrefs } from '../services/colorPreferences';

// Available themes
export const THEMES = {
    dark: {
        id: 'dark',
        name: 'Oscuro',
        icon: '🌑',
        class: 'theme-dark'
    },
    dim: {
        id: 'dim',
        name: 'Intermedio',
        icon: '🌓',
        class: 'theme-dim'
    },
    light: {
        id: 'light',
        name: 'Claro',
        icon: '☀️',
        class: 'theme-light'
    }
};

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(() => {
        // Load saved theme or default to Intermedio (dim)
        const saved = localStorage.getItem('tuvisa-theme');
        return saved && THEMES[saved] ? saved : 'dim';
    });

    // Sync from API on mount
    useEffect(() => {
        const syncPreferences = async () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            try {
                // Get all preferences (Theme + Colors)
                const prefs = await userApi.getPreferences();

                // 1. Sync Colors
                syncFromPrefs(prefs);

                // 2. Sync Theme
                if (prefs && prefs.theme && THEMES[prefs.theme]) {
                    const currentLocal = localStorage.getItem('tuvisa-theme');
                    if (prefs.theme !== currentLocal) {
                        console.log('🎨 Theme synced from cloud:', prefs.theme);
                        setThemeState(prefs.theme);
                    }
                }
            } catch (e) {
                // Silent fail (offline or not logged in)
            }
        };

        syncPreferences();
    }, []);

    useEffect(() => {
        // Remove all theme classes
        Object.values(THEMES).forEach(t => {
            document.documentElement.classList.remove(t.class);
        });

        // Add current theme class
        document.documentElement.classList.add(THEMES[theme].class);

        // Save to localStorage
        localStorage.setItem('tuvisa-theme', theme);
    }, [theme]);

    const setTheme = (newTheme) => {
        setThemeState(newTheme);
        // Persist to Cloud API
        const token = localStorage.getItem('token');
        if (token) {
            userApi.updatePreferences({ theme: newTheme }).catch(err =>
                console.warn('Error saving theme to cloud:', err)
            );
        }
    };

    const value = {
        theme,
        setTheme,
        currentTheme: THEMES[theme],
        themes: THEMES
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;
